import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { object, string } from 'yup';

import { Flex } from 'Src/components/layout/Flex';
import { useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { ListModal } from 'Src/feature/modal/ListModal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { TextModal } from 'Src/feature/modal/TextModal';
import { CurrentOperator } from 'Src/feature/operator/operatorService';
import stashedCredService from 'Src/feature/operator/stashedCredService';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useOperator } from 'Src/feature/operator/useOperator';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { TerminalDetails } from 'Src/feature/pos/TerminalDetails';
import { ConnectionStatus } from 'Src/feature/status/ConnectionStatus';
import { useIsClockedIn } from 'Src/feature/terminal/useIsClockedIn';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useToast } from 'Src/feature/toasts/useToast';
import { ServerError } from 'Src/models/errors';
import { BootDataPrimerTimeClock } from 'Src/network/BootDataPrimerTimeClock';

import styles from './TimeClockLogin.modules.scss';

const sectionId = 'TimeClockLogin';

type LoginForm = {
  pin?: string;
  swipe?: string;
}

const { Form, Field } = getTypedFormComponents<LoginForm>();

/**
 * This is the main pin pad login for the Time Clock
 */
const TimeClockLogin: FC = () => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });
  const toast = useToast();
  const terminalLocation = useTerminal.query.get();
  const { isClockedIn } = useIsClockedIn();
  const cardReader = useCardReader();
  const { currentOperator } = useCurrentOperator();
  const operatorClockIn = useOperator.mutate.clockIn();
  const operatorLogin = useOperator.mutate.login();
  const operatorEndBreak = useOperator.mutate.endBreak();
  const operatorLogout = useOperator.mutate.logout();

  const [showErrorModal, showErrorModalSet] = useState(false);
  const [serverErrorMsg, serverErrorMsgSet] = useState(undefined as undefined | string);
  const [showTimeClockInDlg, showTimeClockInDlgSet] = useState(false);
  const [showEndBreakModal, showEndBreakModalSet] = useState(false);
  const [availableJobPositions, availableJobPositionsSet] = useState([] as CurrentOperator['availableJobPositions']);
  const [lastLoginResponse, lastLoginResponseSet] = useState(undefined as CurrentOperator | undefined);

  const form = useForm<LoginForm>({
    defaultValues: { pin: '' },
    schema: object().shape({
      pin: string().label(t('labels.pin')).optional().min(3),
      swipe: string().optional().min(3),
    }),
  });

  const jobPositionOptions = availableJobPositions?.map(({ id, name }) => ({ value: id as number, label: name }));

  const onStayOnBreak = async () => {
    form.action.setValue('pin', '');
    showEndBreakModalSet(false);
    try {
      await operatorLogout.mutateAsync();
    } catch (err) {
      log.error('onStayOnBreak.logout', err);
      toast.show.serviceError(err, { title: t('posLogin.errors.logout') });
    }
  };

  const login = async (jobPositionId?: number) => {
    try {
      await form.action.submitIfValid(async ({ values }) => {
        const { pin, swipe } = values;
        let prom: Promise<CurrentOperator>;
        if (jobPositionId) {
          prom = operatorClockIn.mutateAsync({
            authNRequest: {
              pin,
              cardCode: swipe,
              jobPositionId,
            },
          });
        } else {
          prom = operatorLogin.mutateAsync({
            authNRequest: {
              pin,
              cardCode: swipe,
            },
          });
        }

        const loginResponse = await prom;
        const { availableJobPositions, shift } = loginResponse;
        // we only stash creds in dev, but good to be thorough
        stashedCredService.clearCreds();

        lastLoginResponseSet(loginResponse);

        if (shift?.isOnBreak) {
          showEndBreakModalSet(true);
        } else if (availableJobPositions && availableJobPositions.length) {
          availableJobPositionsSet(availableJobPositions);
        } else {
          showTimeClockInDlgSet(true);
          form.action.reset({ pin: '' });
          availableJobPositionsSet([]);
        }
      });
    } catch (error) {
      log.error('login', error);
      showErrorModalSet(true);
      const servErr = error as ServerError;
      if (typeof servErr === 'object' && servErr.error) {
        serverErrorMsgSet(servErr.error);
      }
      // reset form
      form.action.reset({ pin: '' });
      availableJobPositionsSet([]);
    }
  };

  useEffect(() => {
    const stashedCreds = stashedCredService.retrieveCreds();
    // if we're already clocked in, the redirect referrer was back to login
    // so don't call login again
    if ((stashedCreds.pin || stashedCreds.swipe) && !(isClockedIn && currentOperator)) {
      log.info('logging in w/ stashed credentials');
      form.action.reset(stashedCreds);
      void login();
    }
    void cardReader.connect();

    return () => {
      void cardReader.dispose();
    };
    // we only want to call this on first mount
    // - cardReader.connect & cardReader.dispose have stable internals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <BootDataPrimerTimeClock />
      <Page
        main={(
          <PageMain
            header={<PageMainHeader end={<ConnectionStatus />} />}
            main={(
              <Flex className={styles.pageContent} horizontal justifyContent="space-between" alignItems="center">
                <TerminalDetails terminal={terminalLocation.data?.terminal} location={terminalLocation.data?.location} />
                <Form usedForm={form} className={styles.form} omitChangeTracking>
                  <Field
                    type="pin"
                    name="pin"
                    onSend={() => {
                      void login();
                    }}
                  />
                </Form>
              </Flex>
            )}
          />
        )}
      />
      <TextModal
        open={showErrorModal}
        title={t('posLogin.invalidLoginDialog.title')}
        color="error"
        textContent={serverErrorMsg || t('posLogin.invalidLoginDialog.defaultMsg')}
        actions={(
          <ModalAction
            color="secondary"
            onClick={() => {
              serverErrorMsgSet(undefined);
              showErrorModalSet(false);
            }}
            label={t('labels.okay')}
          />
        )}
      />
      <TextModal
        data-testid="time-clock-dlg"
        open={showTimeClockInDlg}
        title={t('posLogin.timeClockDialog.titleClockedIn')}
        textContent={lastLoginResponse ? (
          <Trans
            i18nKey="posLogin.timeClockDialog.bodyClockedIn"
            values={{
              jobPositionName: lastLoginResponse?.currentJobPosition?.name || '',
              time: lastLoginResponse.shift ? DateTime.fromMillis(lastLoginResponse.shift.clockIn).toLocaleString(DateTime.TIME_SIMPLE) : '',
            }}
          />
        ) : undefined}
        actions={(
          <ModalAction
            color="secondary"
            onClick={() => {
              lastLoginResponseSet(undefined);
              showTimeClockInDlgSet(false);
            }}
            label={t('labels.okay')}
          />
        )}
      />
      <ListModal
        title="Select a Job Position"
        data-testid="job-position-modal"
        open={!!availableJobPositions && availableJobPositions.length !== 0}
        options={jobPositionOptions || []}
        onOptionClick={(jobPositionOption) => {
          void login(jobPositionOption.value);
        }}
        onAfterOptionClick={() => {
          // do nothing after option click as modal will navigate to a new route
        }}
      />
      <TextModal
        data-testid="end-break-modal"
        open={showEndBreakModal}
        title={t('posLogin.endBreakDialog.titleEndBreak')}
        textContent={t('posLogin.endBreakDialog.bodyEndBreak')}
        onBackDropClick={onStayOnBreak}
        actions={(
          <>
            <ModalAction
              label={t('posLogin.endBreakDialog.actionStayOnBreak')}
              onClick={onStayOnBreak}
            />
            <ModalAction
              color="primary"
              label={t('posLogin.endBreakDialog.actionEndBreak')}
              onClick={async () => {
                showEndBreakModalSet(false);
                try {
                  await operatorEndBreak.mutateAsync();
                  await login();
                } catch (err) {
                  log.error('onEndBreak', err);
                  toast.show.serviceError(err, { title: t('posLogin.errors.endBreak') });
                }
              }}
            />
          </>
        )}
      />
    </>
  );
};

export default TimeClockLogin;
